import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FiBarChart2, FiPlus, FiEdit2, FiTrash2, FiX } from "react-icons/fi";

import Sidebar from "../../Component/Sidebar/Sidebar";
import Header from "../../Component/Header/Header";
import {
  fetchRepresentatives,
  fetchPaymentMethodsSummary,
  fetchExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from "./settlementApi";
import { money, formatDate } from "./settlementFormatters";

const PaymentMethods = ({ onTabChange, embedded = false }) => {
  const [representatives, setRepresentatives] = useState([]);
  const [loadingRepresentatives, setLoadingRepresentatives] = useState(false);
  const [representativeSearch, setRepresentativeSearch] = useState("");
  const [showRepresentativeDropdown, setShowRepresentativeDropdown] = useState(false);

  const [paymentFilters, setPaymentFilters] = useState({
    RepresentativeId: "",
    Date: "",
    FromDate: "",
    ToDate: "",
    Status: "",
    CashAmount: "",
    InstaPayAmount: "",
    VodafoneCashAmount: "",
    TotalPaid: "",
    Notes: "",
  });

  const [paymentMethodsSummary, setPaymentMethodsSummary] = useState(null);
  const [loadingPaymentMethodsSummary, setLoadingPaymentMethodsSummary] = useState(false);
  const [paymentSearched, setPaymentSearched] = useState(false);

  const [expensesData, setExpensesData] = useState({
    totalExpensesAmount: 0,
    totalCount: 0,
    expenses: [],
  });
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expensesSearched, setExpensesSearched] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [savingExpense, setSavingExpense] = useState(false);

  const [expenseFilters, setExpenseFilters] = useState({
    Date: "",
    FromDate: "",
    ToDate: "",
    ExpenseType: "",
    Title: "",
  });

  const emptyExpenseForm = {
    title: "",
    amount: "",
    expenseType: "",
    expenseDate: new Date().toISOString().slice(0, 10),
    notes: "",
  };

  const [expenseForm, setExpenseForm] = useState(emptyExpenseForm);

  const getRepresentatives = async () => {
    try {
      setLoadingRepresentatives(true);
      const data = await fetchRepresentatives();
      setRepresentatives(data);
    } catch (error) {
      console.error("GET REPRESENTATIVES ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "حدث خطأ أثناء جلب المندوبين",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoadingRepresentatives(false);
    }
  };

  useEffect(() => {
    getRepresentatives();
  }, []);

  const filteredRepresentatives = useMemo(() => {
    const search = representativeSearch.trim().toLowerCase();
    if (!search) return representatives;

    return representatives.filter((rep) => {
      const name = String(rep?.fullName || "").toLowerCase();
      const phone = String(rep?.phoneNumber || "").toLowerCase();
      return name.includes(search) || phone.includes(search);
    });
  }, [representatives, representativeSearch]);

  const filteredPaymentDetails = useMemo(() => {
    const details = paymentMethodsSummary?.paymentDetails || [];
    const repSearch = representativeSearch.trim().toLowerCase();
    const notesSearch = String(paymentFilters.Notes || "").trim().toLowerCase();

    return details.filter((item) => {
      const representativeName = String(item?.representativeName || "").toLowerCase();
      const notes = String(item?.notes || "").toLowerCase();

      if (repSearch && !representativeName.includes(repSearch)) return false;
      if (notesSearch && !notes.includes(notesSearch)) return false;

      const checks = [
        [paymentFilters.CashAmount, item?.cashAmount],
        [paymentFilters.InstaPayAmount, item?.instaPayAmount],
        [paymentFilters.VodafoneCashAmount, item?.vodafoneCashAmount],
        [paymentFilters.TotalPaid, item?.totalPaid],
      ];

      return checks.every(([filterValue, itemValue]) => {
        if (filterValue === "" || filterValue == null) return true;
        return Number(itemValue || 0) === Number(filterValue);
      });
    });
  }, [paymentMethodsSummary, representativeSearch, paymentFilters]);

  const getPaymentMethodsSummary = async (filtersToUse = paymentFilters) => {
    try {
      setLoadingPaymentMethodsSummary(true);
      const data = await fetchPaymentMethodsSummary(filtersToUse);
      setPaymentMethodsSummary(data);
      setPaymentSearched(true);
    } catch (error) {
      console.error("GET PAYMENT METHODS SUMMARY ERROR:", error);
      setPaymentMethodsSummary(null);

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء جلب ملخص وسائل الدفع",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoadingPaymentMethodsSummary(false);
    }
  };

  const handlePaymentFilterChange = (name, value) => {
    setPaymentFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetPaymentFilters = () => {
    setPaymentFilters({
      RepresentativeId: "",
      Date: "",
      FromDate: "",
      ToDate: "",
      Status: "",
      CashAmount: "",
      InstaPayAmount: "",
      VodafoneCashAmount: "",
      TotalPaid: "",
      Notes: "",
    });

    setRepresentativeSearch("");
    setShowRepresentativeDropdown(false);
    setPaymentMethodsSummary(null);
    setPaymentSearched(false);
  };

  const paymentTotals = useMemo(() => {
    const details = filteredPaymentDetails;

    return details.reduce(
      (totals, item) => {
        totals.cash += Number(item?.cashAmount || 0);
        totals.instaPay += Number(item?.instaPayAmount || 0);
        totals.vodafoneCash += Number(item?.vodafoneCashAmount || 0);
        totals.totalPaid += Number(item?.totalPaid || 0);
        return totals;
      },
      {
        cash: 0,
        instaPay: 0,
        vodafoneCash: 0,
        totalPaid: 0,
      }
    );
  }, [filteredPaymentDetails]);

  const handleExpenseFilterChange = (name, value) => {
    setExpenseFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getExpenses = async (filtersToUse = expenseFilters) => {
    try {
      setLoadingExpenses(true);
      const data = await fetchExpenses(filtersToUse);

      setExpensesData({
        totalExpensesAmount: Number(data?.totalExpensesAmount || 0),
        totalCount: Number(data?.totalCount || 0),
        expenses: Array.isArray(data?.expenses) ? data.expenses : [],
      });
    } catch (error) {
      console.error("GET EXPENSES ERROR:", error);
      setExpensesData({
        totalExpensesAmount: 0,
        totalCount: 0,
        expenses: [],
      });

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء جلب مصروفات الخزنة",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoadingExpenses(false);
    }
  };

  const handleSearchExpenses = async () => {
    setExpensesSearched(true);
    await getExpenses(expenseFilters);
  };

  const clearExpenseFilters = () => {
    setExpenseFilters({
      Date: "",
      FromDate: "",
      ToDate: "",
      ExpenseType: "",
      Title: "",
    });
    setExpensesData({
      totalExpensesAmount: 0,
      totalCount: 0,
      expenses: [],
    });
    setExpensesSearched(false);
  };

  const openAddExpense = () => {
    setEditingExpense(null);
    setExpenseForm({
      ...emptyExpenseForm,
      expenseDate: new Date().toISOString().slice(0, 10),
    });
    setShowExpenseForm(true);
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      title: expense?.title || "",
      amount: expense?.amount ?? "",
      expenseType: expense?.expenseType || "",
      expenseDate: expense?.expenseDate
        ? String(expense.expenseDate).slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      notes: expense?.notes || "",
    });
    setShowExpenseForm(true);
  };

  const closeExpenseForm = () => {
    if (savingExpense) return;
    setShowExpenseForm(false);
    setEditingExpense(null);
    setExpenseForm(emptyExpenseForm);
  };

  const handleExpenseFormChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();

    if (!expenseForm.title.trim()) {
      Swal.fire({
        icon: "warning",
        title: "بيانات ناقصة",
        text: "من فضلك اكتب اسم المصروف",
        confirmButtonText: "حسناً",
      });
      return;
    }

    if (Number(expenseForm.amount) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "بيانات ناقصة",
        text: "من فضلك أدخل مبلغ المصروف",
        confirmButtonText: "حسناً",
      });
      return;
    }

    try {
      setSavingExpense(true);

      const payload = {
        title: expenseForm.title.trim(),
        amount: Number(expenseForm.amount) || 0,
        expenseType: expenseForm.expenseType.trim(),
        expenseDate: `${expenseForm.expenseDate}T00:00:00`,
        notes: expenseForm.notes.trim(),
      };

      if (editingExpense?.id) {
        await updateExpense(editingExpense.id, payload);
      } else {
        await createExpense(payload);
      }

      Swal.fire({
        icon: "success",
        title: editingExpense ? "تم تعديل المصروف" : "تم إضافة المصروف",
        text: editingExpense
          ? "تم تعديل المصروف بنجاح"
          : "تم إضافة المصروف بنجاح",
        confirmButtonText: "حسناً",
      });

      closeExpenseForm();
      await getExpenses(expenseFilters);
    } catch (error) {
      console.error("SAVE EXPENSE ERROR:", error);

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء حفظ المصروف",
        confirmButtonText: "حسناً",
      });
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async (expense) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "حذف المصروف؟",
      text: `هل أنت متأكد من حذف "${expense?.title || "هذا المصروف"}"؟`,
      showCancelButton: true,
      confirmButtonText: "نعم، احذف",
      cancelButtonText: "إلغاء",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteExpense(expense.id);

      Swal.fire({
        icon: "success",
        title: "تم الحذف",
        text: "تم حذف المصروف بنجاح",
        confirmButtonText: "حسناً",
      });

      await getExpenses(expenseFilters);
    } catch (error) {
      console.error("DELETE EXPENSE ERROR:", error);

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء حذف المصروف",
        confirmButtonText: "حسناً",
      });
    }
  };

  const statusClass = (status) => {
    if (status === "تم التقفيل") return "bg-green-100 text-green-700";
    if (status === "تقفيل جزئي") return "bg-yellow-100 text-yellow-700";
    return "bg-slate-100 text-slate-700";
  };

  const content = (
    <>
          {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">تسوية المندوبين</h1>
          <p className="mt-1 text-sm text-slate-500">
            ملخص ومراجعة المدفوعات حسب وسيلة الدفع والمندوب
          </p>
        </div>
      )}

      {!embedded && (
      <>
      {/* MAIN TABS */}
          <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onTabChange?.("settlement")}
                className="flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold text-slate-600 transition hover:bg-slate-100"
              >
                تسوية المندوب
              </button>

              <button
                type="button"
                className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-bold text-white shadow"
              >
                <FiBarChart2 />
                وسائل الدفع
              </button>
            </div>
          </div>
      </>
      )}

          {/* SUMMARY STRIP */}
          {paymentSearched && (
          <section className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-2 divide-x divide-slate-200 rtl:divide-x-reverse sm:grid-cols-3 lg:grid-cols-5">
              <div className="p-3 text-center">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">إجمالي الكاش</p>
                <p className="mt-1 text-base font-black text-green-700">
                  {money(paymentTotals.cash)} ج.م
                </p>
              </div>
              <div className="p-3 text-center">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">إجمالي InstaPay</p>
                <p className="mt-1 text-base font-black text-blue-700">
                  {money(paymentTotals.instaPay)} ج.م
                </p>
              </div>
              <div className="p-3 text-center">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">إجمالي Vodafone Cash</p>
                <p className="mt-1 text-base font-black text-red-700">
                  {money(paymentTotals.vodafoneCash)} ج.م
                </p>
              </div>
              <div className="p-3 text-center">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">إجمالي المدفوع</p>
                <p className="mt-1 text-base font-black text-purple-700">
                  {money(paymentTotals.totalPaid)} ج.م
                </p>
              </div>
              <div className="p-3 text-center">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">عدد التسويات</p>
                <p className="mt-1 text-base font-black text-orange-700">
                  {filteredPaymentDetails.length}
                </p>
              </div>
            </div>
          </section>

          )}

          {/* PAYMENT DETAILS + COMPACT FILTERS */}
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  تفاصيل المدفوعات
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  جميع المندوبين والتسويات التي تم إرجاعها بواسطة ملخص وسائل الدفع
                </p>
              </div>

              <button
                type="button"
                onClick={openAddExpense}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <FiPlus size={16} />
                إضافة مصروف
              </button>
            </div>


            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[1250px] text-right text-sm">
                <thead>
                  {/* FILTER ROW */}
                  <tr className="border-b border-slate-200 bg-white">
                    <th className="p-1 text-center">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        aria-label="اختيار الكل"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={representativeSearch}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRepresentativeSearch(value);

                          const matched = representatives.find(
                            (rep) =>
                              String(rep?.fullName || "").trim().toLowerCase() ===
                              value.trim().toLowerCase()
                          );

                          handlePaymentFilterChange(
                            "RepresentativeId",
                            matched ? String(matched.id) : ""
                          );
                        }}
                        list="payment-representatives"
                        placeholder="المندوب"
                        className="h-8 w-full min-w-[150px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                      <datalist id="payment-representatives">
                        {representatives.map((rep) => (
                          <option key={rep.id} value={rep.fullName || ""} />
                        ))}
                      </datalist>
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="date"
                        value={paymentFilters.Date}
                        onChange={(e) =>
                          handlePaymentFilterChange("Date", e.target.value)
                        }
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="date"
                        value={paymentFilters.FromDate}
                        onChange={(e) =>
                          handlePaymentFilterChange("FromDate", e.target.value)
                        }
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        value={paymentFilters.CashAmount || ""}
                        onChange={(e) =>
                          handlePaymentFilterChange("CashAmount", e.target.value)
                        }
                        placeholder="الكاش"
                        className="h-8 w-full min-w-[95px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        value={paymentFilters.InstaPayAmount || ""}
                        onChange={(e) =>
                          handlePaymentFilterChange("InstaPayAmount", e.target.value)
                        }
                        placeholder="InstaPay"
                        className="h-8 w-full min-w-[95px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        value={paymentFilters.VodafoneCashAmount || ""}
                        onChange={(e) =>
                          handlePaymentFilterChange("VodafoneCashAmount", e.target.value)
                        }
                        placeholder="فودافون"
                        className="h-8 w-full min-w-[105px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        value={paymentFilters.TotalPaid || ""}
                        onChange={(e) =>
                          handlePaymentFilterChange("TotalPaid", e.target.value)
                        }
                        placeholder="الإجمالي"
                        className="h-8 w-full min-w-[100px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <select
                        value={paymentFilters.Status}
                        onChange={(e) =>
                          handlePaymentFilterChange("Status", e.target.value)
                        }
                        className="h-8 w-full min-w-[115px] rounded-md border border-slate-300 bg-white px-1 text-xs text-slate-600 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      >
                        <option value="">الحالة</option>
                        <option value="تم التقفيل">تم التقفيل</option>
                        <option value="تقفيل جزئي">تقفيل جزئي</option>
                      </select>
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={paymentFilters.Notes || ""}
                        onChange={(e) =>
                          handlePaymentFilterChange("Notes", e.target.value)
                        }
                        placeholder="الملاحظات"
                        className="h-8 w-full min-w-[120px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                  
                    
                  </tr>
  <div className="sticky right-0 z-50 flex justify-end bg-white border-b border-slate-200 p-2">
                        <div className="flex items-center gap-1">
                        
                        <button
                            type="button"
                            onClick={() => getPaymentMethodsSummary(paymentFilters)}
                            disabled={loadingPaymentMethodsSummary || loadingRepresentatives}
                            className="h-8 rounded-md bg-blue-600 px-3 text-xs font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loadingPaymentMethodsSummary ? "..." : "بحث"}
                        </button>

                        <button
                            type="button"
                            onClick={resetPaymentFilters}
                            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-[11px] font-bold text-slate-600 transition hover:bg-slate-50"
                            title="مسح الفلاتر"
                        >
                            مسح
                        </button>

                        </div>
                    </div>
                  {/* TABLE HEADER */}
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">المندوب</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">تاريخ التسوية</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">تاريخ الدفع</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">كاش</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">InstaPay</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Vodafone Cash</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">الإجمالي</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">الحالة</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">الملاحظات</th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">تاريخ الإنشاء</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {!paymentSearched ? (
                    <tr>
                      <td colSpan="11" className="px-4 py-12 text-center">
                        <div className="mx-auto flex max-w-md flex-col items-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                            <FiBarChart2 size={24} />
                          </div>
                          <p className="mt-3 text-sm font-bold text-slate-700">
                            اضغط على زر بحث لعرض تفاصيل المدفوعات
                          </p>
                          <p className="mt-1 text-xs text-slate-400">
                            لن يتم تحميل أي بيانات قبل تنفيذ البحث
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredPaymentDetails.length > 0 ? (
                    filteredPaymentDetails.map((item, index) => (
                      <tr
                        key={`${item.settlementId}-${item.paymentId}-${index}`}
                        className="hover:bg-slate-50"
                      >
                        <td className="p-1 text-center font-bold text-slate-500">{index + 1}</td>
                        <td className="p-1 text-center font-bold text-slate-800">{item.representativeName || "-"}</td>
                        <td className="p-1 text-center text-slate-600">{formatDate(item.settlementDate)}</td>
                        <td className="p-1 text-center text-slate-600">{formatDate(item.paymentDate)}</td>
                        <td className="p-1 text-center font-bold text-green-600">{money(item.cashAmount)} ج.م</td>
                        <td className="p-1 text-center font-bold text-blue-600">{money(item.instaPayAmount)} ج.م</td>
                        <td className="p-1 text-center font-bold text-red-600">{money(item.vodafoneCashAmount)} ج.م</td>
                        <td className="p-1 text-center">
                          <span className="rounded-lg bg-purple-50 px-3 py-1 font-black text-purple-700">
                            {money(item.totalPaid)} ج.م
                          </span>
                        </td>
                        <td className="p-1 text-center">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusClass(item.settlementStatus)}`}>
                            {item.settlementStatus || "-"}
                          </span>
                        </td>
                        <td className="max-w-[220px] p-1 text-center text-slate-500">{item.notes || "-"}</td>
                        <td className="p-1 text-center text-xs text-slate-500">{formatDate(item.dateCreated)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="px-4 py-12 text-center text-slate-500">
                        {loadingPaymentMethodsSummary
                          ? "جاري تحميل البيانات..."
                          : paymentMethodsSummary?.paymentDetails?.length
                            ? "لا توجد نتائج مطابقة للفلاتر"
                            : "لا توجد بيانات مدفوعات"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* EXPENSES */}
          <section className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 p-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">مصروفات الخزنة</h2>
                <p className="mt-1 text-sm text-slate-500">
                  إضافة ومراجعة مصروفات الخزنة بنفس نظام الفلاتر والجدول
                </p>
              </div>

            </div>

            <div className="grid grid-cols-2 divide-x divide-slate-200 border-b border-slate-200 rtl:divide-x-reverse">
              <div className="p-3 text-center">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                  إجمالي المصروفات
                </p>
                <p className="mt-1 text-base font-black text-red-700">
                  {money(expensesData.totalExpensesAmount)} ج.م
                </p>
              </div>
              <div className="p-3 text-center">
                <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                  عدد المصروفات
                </p>
                <p className="mt-1 text-base font-black text-slate-700">
                  {expensesData.totalCount}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] text-right text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-white">
                    <th className="p-1 text-center">
                      <input
                        type="date"
                        value={expenseFilters.Date}
                        onChange={(e) =>
                          handleExpenseFilterChange("Date", e.target.value)
                        }
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={expenseFilters.Title}
                        onChange={(e) =>
                          handleExpenseFilterChange("Title", e.target.value)
                        }
                        placeholder="العنوان"
                        className="h-8 w-full min-w-[150px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={expenseFilters.ExpenseType}
                        onChange={(e) =>
                          handleExpenseFilterChange("ExpenseType", e.target.value)
                        }
                        placeholder="نوع المصروف"
                        className="h-8 w-full min-w-[130px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="date"
                        value={expenseFilters.FromDate}
                        onChange={(e) =>
                          handleExpenseFilterChange("FromDate", e.target.value)
                        }
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="date"
                        value={expenseFilters.ToDate}
                        onChange={(e) =>
                          handleExpenseFilterChange("ToDate", e.target.value)
                        }
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={handleSearchExpenses}
                          disabled={loadingExpenses}
                          className="h-8 rounded-md bg-blue-600 px-3 text-[11px] font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {loadingExpenses ? "..." : "بحث"}
                        </button>
                        <button
                          type="button"
                          onClick={clearExpenseFilters}
                          className="h-8 rounded-md border border-slate-300 bg-white px-2 text-[11px] font-bold text-slate-600 transition hover:bg-slate-50"
                        >
                          مسح
                        </button>
                      </div>
                    </th>

                    <th className="p-1 text-center" />
                  </tr>

                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      تاريخ المصروف
                    </th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      العنوان
                    </th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      النوع
                    </th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      المبلغ
                    </th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      الملاحظات
                    </th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      تاريخ الإنشاء
                    </th>
                    <th className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      إجراءات
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {expensesData.expenses.length > 0 ? (
                    expensesData.expenses.map((expense) => (
                      <tr key={expense.id} className="hover:bg-slate-50">
                        <td className="p-1 text-center text-slate-600">
                          {formatDate(expense.expenseDate)}
                        </td>
                        <td className="p-1 text-center font-bold text-slate-800">
                          {expense.title || "-"}
                        </td>
                        <td className="p-1 text-center font-bold text-slate-700">
                          {expense.expenseType || "-"}
                        </td>
                        <td className="p-1 text-center font-black text-red-600">
                          {money(expense.amount)} ج.م
                        </td>
                        <td className="max-w-[260px] p-1 text-center text-slate-500">
                          {expense.notes || "-"}
                        </td>
                        <td className="p-1 text-center text-xs text-slate-500">
                          {formatDate(expense.dateCreated)}
                        </td>
                        <td className="p-1 text-center">
                          <div className="inline-flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEditExpense(expense)}
                              title="تعديل المصروف"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-600 transition hover:border-blue-400 hover:text-blue-600"
                            >
                              <FiEdit2 size={14} />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteExpense(expense)}
                              title="حذف المصروف"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-600 transition hover:border-red-400 hover:text-red-600"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 py-10 text-center text-slate-500">
                        {loadingExpenses
                          ? "جاري تحميل المصروفات..."
                          : expensesSearched
                            ? "لا توجد مصروفات مطابقة للفلاتر"
                            : "اضغط بحث لعرض المصروفات"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>


            {showExpenseForm && (
              <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeExpenseForm();
                }}
              >
                <div
                  dir="rtl"
                  className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
                >
                  <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">
                        {editingExpense ? "تعديل المصروف" : "إضافة مصروف"}
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        أدخل بيانات المصروف ثم اضغط حفظ
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={closeExpenseForm}
                      disabled={savingExpense}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
                    >
                      <FiX size={17} />
                    </button>
                  </div>

                  <form onSubmit={handleSaveExpense} className="p-5">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold text-slate-600">
                          اسم المصروف <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="title"
                          value={expenseForm.title}
                          onChange={handleExpenseFormChange}
                          placeholder="مثال: بنزين"
                          autoFocus
                          className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-bold text-slate-600">
                          المبلغ <span className="text-red-500">*</span>
                        </label>
                        <input
                          name="amount"
                          type="number"
                          min="0"
                          step="0.01"
                          value={expenseForm.amount}
                          onChange={handleExpenseFormChange}
                          placeholder="0.00"
                          className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-bold text-slate-600">
                          نوع المصروف
                        </label>
                        <input
                          name="expenseType"
                          value={expenseForm.expenseType}
                          onChange={handleExpenseFormChange}
                          placeholder="مثال: تشغيل"
                          className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-bold text-slate-600">
                          تاريخ المصروف
                        </label>
                        <input
                          name="expenseDate"
                          type="date"
                          value={expenseForm.expenseDate}
                          onChange={handleExpenseFormChange}
                          className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="mb-1.5 block text-xs font-bold text-slate-600">
                          ملاحظات
                        </label>
                        <textarea
                          name="notes"
                          value={expenseForm.notes}
                          onChange={handleExpenseFormChange}
                          placeholder="أدخل أي ملاحظات إضافية..."
                          rows={3}
                          className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                    </div>

                    <div className="mt-5 flex justify-start gap-2 border-t border-slate-100 pt-4">
                      <button
                        type="button"
                        onClick={closeExpenseForm}
                        disabled={savingExpense}
                        className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        disabled={savingExpense}
                        className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {savingExpense
                          ? "جاري الحفظ..."
                          : editingExpense
                            ? "حفظ التعديل"
                            : "حفظ المصروف"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </section>
        </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="apDiv ChatApp min-h-screen bg-slate-50">
      <Sidebar />
      <div className="body_container container" dir="rtl">
        <div className="Header">
          <Header />
        </div>
        <main className="px-4 py-6 md:px-6 lg:px-8">
          {content}
        </main>
      </div>
    </div>
  );
};

export default PaymentMethods;
